import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { ReactComponent as BookmarkIcon } from "../../../assets/icons/bookmarkList.svg";
import { ReactComponent as CloseIcon } from "../../../assets/icons/close.svg";
import { ReactComponent as DownloadIcon } from "../../../assets/icons/download.svg";
import { ReactComponent as FaqIcon } from "../../../assets/icons/faq.svg";
import { ReactComponent as InfoIcon } from "../../../assets/icons/info.svg";
import { ReactComponent as LanguageIcon } from "../../../assets/icons/language.svg";
import { ReactComponent as NotebookIcon } from "../../../assets/icons/notebook.svg";
import { ReactComponent as PrintIcon } from "../../../assets/icons/print.svg";
import useReaderStore from "../../../context/readerStore";
import { LANGUAGE_OPTIONS } from "../../../i18n/languages";
import { isFeatureAllowed } from "../../../services/drmPolicyService";
import { showComingSoonNotification } from "../../../services/notificationService";
import "./MobileMenu.css";

const MenuItem = ({
  icon: Icon,
  label,
  onClick,
  comingSoon = false,
  hasChevron = false,
  iconClassName,
  /** 40×40 artboard icons (e.g. notebook, info) need a larger box to match peers */
  iconSize = 20,
}) => (
  <li>
    <button
      type="button"
      className={`mobile-menu-item${comingSoon ? " is-coming-soon" : ""}`}
      onClick={onClick}
      title={comingSoon ? label : undefined}
    >
      <Icon
        className={iconClassName}
        width={iconSize}
        height={iconSize}
        aria-hidden="true"
      />
      <span className="mobile-menu-item-label">{label}</span>
      {hasChevron && (
        <span className="mobile-menu-item-chevron" aria-hidden="true">
          ›
        </span>
      )}
    </button>
  </li>
);

const MobileMenu = ({ onClose, onToggleHelp }) => {
  const { t, i18n } = useTranslation();
  const [submenu, setSubmenu] = useState(null);
  const {
    toggleBookmarksSidebar,
    toggleNotesSidebar,
    togglePublicationDetailsSidebar,
    documentType,
    documentName,
    currentPage,
    totalPages,
    drmPolicy,
  } = useReaderStore();

  const displayName = documentName || t("common.document");
  const currentLanguage = i18n.resolvedLanguage || i18n.language;
  const downloadAllowed = isFeatureAllowed(drmPolicy, "download");
  const printingAllowed = isFeatureAllowed(drmPolicy, "printing");
  const bookmarksAllowed = isFeatureAllowed(drmPolicy, "bookmarks");
  const notesAllowed = isFeatureAllowed(drmPolicy, "notes");

  const runAndClose = useCallback(
    (action) => {
      action?.();
      onClose();
    },
    [onClose],
  );

  const handleComingSoon = useCallback(
    (featureKey) => {
      showComingSoonNotification(featureKey);
      onClose();
    },
    [onClose],
  );

  const handleLanguageChange = useCallback(
    (languageCode) => {
      i18n.changeLanguage(languageCode);
      setSubmenu(null);
    },
    [i18n],
  );

  return (
    <div className="mobile-menu-overlay" onClick={onClose}>
      <div className="mobile-menu-content" onClick={(e) => e.stopPropagation()}>
        <div className="mobile-menu-header">
          {submenu ? (
            <>
              <button
                type="button"
                className="mobile-menu-back"
                onClick={() => setSubmenu(null)}
                aria-label={t("common.back")}
              >
                ‹
              </button>
              <h3>{t("common.language")}</h3>
              <button
                type="button"
                className="mobile-menu-close"
                onClick={onClose}
                aria-label={t("menu.close")}
              >
                <CloseIcon width={24} height={24} />
              </button>
            </>
          ) : (
            <>
              <h3>{t("common.menu")}</h3>
              <button
                type="button"
                className="mobile-menu-close"
                onClick={onClose}
                aria-label={t("menu.close")}
              >
                <CloseIcon width={24} height={24} />
              </button>
            </>
          )}
        </div>

        {!submenu && documentType && (
          <div className="mobile-menu-doc">
            <div className="mobile-menu-doc-row">
              <div className="mobile-menu-doc-name" title={displayName}>
                {displayName}
              </div>
              <div className={`document-badge ${documentType}`}>
                {documentType.toUpperCase()}
              </div>
            </div>
            {totalPages > 0 && (
              <div className="mobile-menu-doc-meta">
                {t("menu.documentPageCount", {
                  current: currentPage,
                  total: totalPages,
                })}
              </div>
            )}
          </div>
        )}

        {submenu === "language" ? (
          <ul className="mobile-menu-list mobile-menu-list--options">
            {LANGUAGE_OPTIONS.map((lang) => {
              const isActive = currentLanguage === lang.code;
              return (
                <li key={lang.code}>
                  <button
                    type="button"
                    className={`mobile-menu-option${isActive ? " is-active" : ""}`}
                    onClick={() => handleLanguageChange(lang.code)}
                  >
                    <span>{lang.label}</span>
                    {isActive && (
                      <span className="mobile-menu-option-check" aria-hidden="true">
                        ✓
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
        ) : (
          <ul className="mobile-menu-list">
            <MenuItem
              icon={LanguageIcon}
              label={t("common.language")}
              onClick={() => setSubmenu("language")}
              hasChevron
            />

            {downloadAllowed && (
              <MenuItem
                icon={DownloadIcon}
                label={t("toolbar.download")}
                comingSoon
                onClick={() => handleComingSoon("download")}
              />
            )}

            <MenuItem
              icon={InfoIcon}
              label={t("publicationDetails.title")}
              iconClassName="mobile-menu-icon--artboard"
              iconSize={52}
              onClick={() => runAndClose(togglePublicationDetailsSidebar)}
            />

            {bookmarksAllowed && (
              <MenuItem
                icon={BookmarkIcon}
                label={t("common.bookmarks")}
                onClick={() => runAndClose(toggleBookmarksSidebar)}
              />
            )}

            {notesAllowed && (
              <MenuItem
                icon={NotebookIcon}
                label={t("rightToolbar.myNotebook")}
                iconClassName="mobile-menu-icon--artboard"
                iconSize={52}
                onClick={() => runAndClose(toggleNotesSidebar)}
              />
            )}

            {printingAllowed && (
              <MenuItem
                icon={PrintIcon}
                label={t("toolbar.print")}
                comingSoon
                onClick={() => handleComingSoon("print")}
              />
            )}

            {typeof onToggleHelp === "function" && (
              <MenuItem
                icon={FaqIcon}
                label={t("mobile.faq")}
                onClick={() => runAndClose(onToggleHelp)}
              />
            )}
          </ul>
        )}
      </div>
    </div>
  );
};

export default MobileMenu;
